import React from 'react';

import { routes } from '@aline/routes';
import type { Location } from 'react-router-dom';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import {
  GetStartedRoute,
  Identifiers,
  LaunchTemplateQuickStart,
  Parties,
  Reports,
  RepositoryPage,
  SendForSignaturePage,
  WorkflowsPage,
} from 'src/components';
import { Admin } from 'src/components/admin';
import { Playbooks } from 'src/components/admin/playbooks/Playbooks';
import { ChatPage } from 'src/components/chat';
import { CommunityPage } from 'src/components/community/pages/CommunityPage';
import { Dashboard } from 'src/components/dashboard';
import { Reminders } from 'src/components/reminders';
import { createRelativeRouter } from 'src/helpers/router-helpers';
import { makeStyles } from 'tss-react/mui';

import { CheckoutDialog } from '../admin/billing/Checkout';
import { UserAlerts } from '../dashboard/UserAlerts';
import { PickContractWorkflowAction } from '../quick-start/PickworkflowAction';
import { TemplatePage } from '../template/TemplatePage';
import { TaskLandingPage } from '../workflow/task-landing/TaskLandingPage';
import { WorkflowDetailPage } from '../workflow/workflow-detail/WorkflowDetailPage';

const useStyles = makeStyles()(() => ({
  contentContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    height: '100%',
    // Allow content to expand to the full width of the remaining layout area
    minWidth: 0,
    position: 'relative',
  },
}));

const { toRelativePath: toRelativeHomePath } = createRelativeRouter(routes.Home, {
  nestedRoutes: true,
});

export function MainPage(): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.contentContainer}>
      <UserAlerts />
      <CheckoutDialog />
      <MainPageRoutes />
    </div>
  );
}

/**
 * Just the route definitions for the main page, without UserAlerts/CheckoutDialog.
 * Used by TabContentRoutes where those components live in AppLayout instead.
 */
export function MainPageContent(): JSX.Element {
  const { classes } = useStyles();
  const location = useLocation();

  /**
   * The whole location, not just `pathname`: `<Routes location>` replaces the location context
   * for every descendant, so dropping search/hash here hides a tab's one-shot deep-link params
   * (e.g. `workflowDrilldown`) from the page that is supposed to consume them.
   */
  return (
    <div className={classes.contentContainer}>
      <MainPageRoutes location={location} />
    </div>
  );
}

function MainPageRoutes({ location }: { location?: Partial<Location> | string }): JSX.Element {
  return (
    <Routes location={location}>
      <Route
        element={<SendForSignaturePage />}
        path={toRelativeHomePath(routes.SendForSignatureWorkflow)}
      />
      <Route
        element={<PickContractWorkflowAction />}
        path={toRelativeHomePath(routes.QuickStartPickWorkflowAction)}
      />
      <Route
        element={<LaunchTemplateQuickStart />}
        path={toRelativeHomePath(routes.QuickStartLaunchTemplateIndex)}
      />
      <Route element={<ChatPage />} path={toRelativeHomePath(routes.ChatIndex)} />
      <Route element={<Dashboard />} path={toRelativeHomePath(routes.DashboardIndex)} />
      <Route element={<RepositoryPage />} path={toRelativeHomePath(routes.RepositoryIndex)} />
      <Route
        element={<WorkflowsPage />}
        path={toRelativeHomePath(routes.WorkflowIndexMustRedirect)}
      />
      <Route element={<WorkflowDetailPage />} path={toRelativeHomePath(routes.WorkflowDetail)} />
      <Route element={<TaskLandingPage />} path={toRelativeHomePath(routes.TaskLanding)} />
      <Route element={<TemplatePage />} path={toRelativeHomePath(routes.TemplateIndex)} />
      <Route
        element={<WorkflowDetailPage />}
        path={toRelativeHomePath(routes.TemplateSelectedTemplateIndex)}
      />
      <Route element={<Admin />} path={toRelativeHomePath(routes.AdminIndex)} />
      <Route element={<Identifiers />} path={toRelativeHomePath(routes.IdentifierIndex)} />
      <Route element={<Parties />} path={toRelativeHomePath(routes.PartyIndex)} />
      <Route element={<Reminders />} path={toRelativeHomePath(routes.RemindersIndex)} />
      <Route element={<Reports />} path={toRelativeHomePath(routes.ReportIndex)} />
      <Route element={<Playbooks />} path={toRelativeHomePath(routes.PlaybooksIndex)} />
      <Route element={<CommunityPage />} path={toRelativeHomePath(routes.CommunityIndex)} />
      <Route element={<GetStartedRoute />} path={toRelativeHomePath(routes.GetStarted)} />
      <Route element={<Navigate replace to={routes.ChatIndex} />} path="/" />
    </Routes>
  );
}
